import React, { useState } from 'react';
import { Box, Typography, Button, IconButton, CircularProgress, useTheme, Tooltip } from '@mui/material';
import {
  Block as BlockIcon, LockOpen as UnblockIcon,
  DeleteOutline as DeleteIcon, FileDownload as ExportIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { IOCEntry } from '../../types';
import { IOCsService } from '../../services/iocsService';
import { colorUtils } from '../../theme';

interface IOCBulkActionBarProps {
  selectedIds: Set<string>;
  allIocs: IOCEntry[];
  onClearSelection: () => void;
  onActionComplete: () => void;
}

const IOCBulkActionBar: React.FC<IOCBulkActionBarProps> = ({
  selectedIds, allIocs, onClearSelection, onActionComplete,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState<string | null>(null);

  if (selectedIds.size === 0) return null;

  const handleBulkAction = async (action: 'block' | 'unblock' | 'delete') => {
    if (action === 'delete' && !window.confirm(`Delete ${selectedIds.size} IOC${selectedIds.size > 1 ? 's' : ''}? This cannot be undone.`)) return;
    setLoading(action);
    try {
      const result = await IOCsService.bulkAction({ action, ids: Array.from(selectedIds) });
      if (result.failed.length > 0) {
        console.warn(`[BulkAction] ${result.failed.length} failed:`, result.failed);
      }
      onClearSelection();
      onActionComplete();
    } catch (e) {
      console.error('[BulkAction] failed:', e);
    } finally {
      setLoading(null);
    }
  };

  const handleExportSelected = () => {
    const selected = allIocs.filter(ioc => selectedIds.has(ioc.id));
    const headers = ['ioc_value', 'ioc_type', 'source', 'reputation_score', 'is_malicious', 'is_blocked', 'blocked_until', 'country_code', 'isp', 'tags', 'notes', 'created_at'];
    const rows = selected.map(ioc => [
      ioc.ioc_value, ioc.ioc_type, ioc.source,
      ioc.reputation_score ?? '', ioc.is_malicious ?? '',
      ioc.is_currently_blocked, ioc.blocked_until ?? '',
      ioc.country_code ?? '', ioc.isp ?? '',
      (ioc.tags || []).join(';'), ioc.notes ?? '',
      ioc.created_at,
    ]);

    const csv = [headers, ...rows].map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `iocs_selected_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const btnSx = {
    textTransform: 'none' as const,
    fontWeight: 600,
    fontSize: '0.78rem',
    height: 32,
  };

  return (
    <Box sx={{
      position: 'fixed',
      bottom: 28,
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: theme.zIndex.snackbar,
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: 2.5,
      py: 1.5,
      borderRadius: 2,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.border?.strong ?? theme.palette.divider}`,
      boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      pointerEvents: 'auto',
    }}>
      <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.text.secondary, mr: 1, whiteSpace: 'nowrap' }}>
        {selectedIds.size} selected
      </Typography>

      <Button
        size="small" variant="outlined"
        startIcon={loading === 'block' ? <CircularProgress size={13} /> : <BlockIcon sx={{ fontSize: 15 }} />}
        disabled={!!loading}
        onClick={() => handleBulkAction('block')}
        sx={{ ...btnSx, borderColor: colorUtils.withOpacity(theme.palette.error.main, 0.4), color: theme.palette.error.main }}
      >
        Block
      </Button>

      <Button
        size="small" variant="outlined"
        startIcon={loading === 'unblock' ? <CircularProgress size={13} /> : <UnblockIcon sx={{ fontSize: 15 }} />}
        disabled={!!loading}
        onClick={() => handleBulkAction('unblock')}
        sx={{ ...btnSx, borderColor: colorUtils.withOpacity(theme.palette.warning.main, 0.4), color: theme.palette.warning.main }}
      >
        Unblock
      </Button>

      <Button
        size="small" variant="outlined"
        startIcon={loading === 'delete' ? <CircularProgress size={13} /> : <DeleteIcon sx={{ fontSize: 15 }} />}
        disabled={!!loading}
        onClick={() => handleBulkAction('delete')}
        sx={{ ...btnSx, borderColor: colorUtils.withOpacity(theme.palette.text.disabled, 0.4), color: theme.palette.text.secondary }}
      >
        Delete
      </Button>

      <Button
        size="small" variant="outlined"
        startIcon={<ExportIcon sx={{ fontSize: 15 }} />}
        disabled={!!loading}
        onClick={handleExportSelected}
        sx={{ ...btnSx, borderColor: colorUtils.withOpacity(theme.palette.primary.main, 0.4), color: theme.palette.primary.main }}
      >
        Export
      </Button>

      <Tooltip title="Clear selection">
        <IconButton size="small" onClick={onClearSelection} sx={{ ml: 0.5, color: theme.palette.text.tertiary }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default IOCBulkActionBar;
